import { EConfigurationType, EFilterRootOption } from '../../data/Constants';
import { AxiosResponse } from 'axios';

import type {
  ITreeNode,
  IFilterOption,
  IFilterNode,
  IMyConfigurationFilter,
  IProductFilter,
  IProduct,
  IProductFilterOptions,
  ISimpleTreeNode,
} from '../../../types';

const configNameRegEx = /^(\(?[A-Za-z0-9]+\)?)([ _-]\(?[A-Za-z0-9]+\)?)*$/;

/**
 * Handles the selection and deselection of multi-select filter items including root node.
 * Manages the state of selected filter codes based on user interactions.
 *
 * @param {Array<string>} itemIds - List of selected filter item codes
 * @param {ISimpleTreeNode} treeNode - Current tree node where the action was performed
 * @param {IFilterOption} filteredCodes - All currently filtered codes
 * @param {IFilterOption} availableFilterCodes - All available codes for filtering
 * @returns {IFilterOption} Updated filter options with selected codes to display in the UI
 */

export const handleSelectionOfMultiSelectOnChange = (
  itemIds: Array<string>,
  treeNode: ISimpleTreeNode,
  filteredCodes: IFilterOption,
  availableFilterCodes: IFilterOption
): IFilterOption => {
  const selectedCodes = { ...filteredCodes };
  if (
    itemIds.includes(treeNode.enumValue) &&
    !filteredCodes[treeNode.type].includes(treeNode.enumValue)
  ) {
    selectedCodes[treeNode.type] = [...availableFilterCodes[treeNode.type], treeNode.enumValue];
  } else if (
    itemIds.includes(treeNode.enumValue) &&
    selectedCodes[treeNode.type].includes(treeNode.enumValue)
  ) {
    selectedCodes[treeNode.type] = itemIds.filter((code) => code !== treeNode.enumValue);
  } else if (
    !itemIds.includes(treeNode.enumValue) &&
    selectedCodes[treeNode.type].includes(treeNode.enumValue)
  ) {
    selectedCodes[treeNode.type] = [];
  } else if (
    !itemIds.includes(treeNode.enumValue) &&
    itemIds.length === availableFilterCodes[treeNode.type].length
  ) {
    selectedCodes[treeNode.type] = [...availableFilterCodes[treeNode.type], treeNode.enumValue];
  } else {
    selectedCodes[treeNode.type] = [...itemIds];
  }
  return selectedCodes;
};

/**
 * Prepares and extracts filter codes available for the product portfolio.
 * Processes the product filter tree structure and returns flattened arrays of codes.
 *
 * @param {Object} productFilter - Object containing all root filter elements
 * @param {IFilterNode[]} productFilter.channels - Array of channel filter nodes
 * @param {ITreeNode[]} productFilter.business - Array of business filter nodes
 * @returns {IProductFilter} Object containing arrays of channel and business codes available for filtering
 */

export const prepareTreeCodes = (productFilter: {
  channels: IFilterNode[];
  business: ITreeNode[];
}): IProductFilter => {
  const filterCodes: IProductFilter = { channels: [], business: [] };
  if (!productFilter) {
    return filterCodes;
  }
  productFilter.channels.forEach((node: IFilterNode) => {
    filterCodes.channels.push(node.code);
  });

  productFilter.business.forEach((node: ITreeNode) => {
    filterCodes.business.push(node.code);
  });

  return filterCodes;
};

/**
 * Prepares and extracts filter codes available for "My Configuration" section.
 * Processes configuration filter tree structure and returns flattened arrays of codes.
 *
 * @param {Object} myConfigurationFilter - Object containing all root configuration filter elements
 * @param {IFilterNode[]} myConfigurationFilter.channels - Array of channel filter nodes
 * @param {IFilterNode[]} myConfigurationFilter.applications - Array of application filter nodes
 * @param {IFilterNode[]} myConfigurationFilter.states - Array of state filter nodes
 * @returns {IMyConfigurationFilter} Object containing arrays of channel, application, and state codes available for filtering
 */

export const prepareMyConfigurationTreeCodes = (myConfigurationFilter: {
  channels: IFilterNode[];
  applications: IFilterNode[];
  states: IFilterNode[];
}): IMyConfigurationFilter => {
  const filterCodes: IMyConfigurationFilter = { channels: [], applications: [], states: [] };
  if (!myConfigurationFilter) {
    return filterCodes;
  }
  myConfigurationFilter.channels.forEach((node: IFilterNode) => {
    filterCodes.channels.push(node.code);
  });

  myConfigurationFilter.applications.forEach((node: IFilterNode) => {
    filterCodes.applications.push(node.code);
  });

  myConfigurationFilter.states.forEach((node: IFilterNode) => {
    filterCodes.states.push(node.code);
  });

  return filterCodes;
};

/**
 * Retrieves the list of available countries for a selected product.
 * Searches through the product portfolio to find matching product and returns its countries.
 *
 * @param {IProduct[]} prodList - The complete product portfolio list
 * @param {string} productId - The ID of the selected product
 * @returns {IProduct['countries'] | []} Array of available countries for the product, or empty array if product not found
 */

export const getAvailableCountries = (
  prodList: IProduct[],
  productId: string
): IProduct['countries'] | [] => {
  const productData: IProduct | undefined = prodList.find((x) => {
    return x.productId === productId;
  });
  return productData ? productData.countries : [];
};

/**
 * Adds article group codes (agCodes) of selected business filter items to the request payload.
 * This method includes associated agCodes beyond those directly selected by the user.
 *
 * @param {IProductFilterOptions} prodFilter - The product filter options containing business and channel filters
 * @param {IProductFilter} selectedCodes - The currently selected filter codes
 * @returns {IProductFilter} Updated selected codes with article group codes merged in
 */

export const getSelectedCodesWithAgCodes = (
  prodFilter: IProductFilterOptions,
  selectedCodes: IProductFilter
): IProductFilter => {
  const type = EFilterRootOption.Business.toLowerCase();
  prodFilter.business.forEach((item: ITreeNode) => {
    if (selectedCodes[type].includes(item.code) && item.articleGroups) {
      selectedCodes[type] = [...selectedCodes[type], ...item.articleGroups];
    }
  });
  return selectedCodes;
};

/**
 * Retrieves the display names of selected filter items for collapsed mode display.
 * Matches selected codes against tree items to extract their display names.
 *
 * @param {ISimpleTreeNode} treeItem - The simple tree node containing filter items
 * @param {IFilterOption} selectedCodes - The selected codes from the filter
 * @returns {Array<string> | []} Array of names for selected codes to display in UI, or empty array
 */

export const getSelectedCodeNames = (
  treeItem: ISimpleTreeNode,
  selectedCodes: IFilterOption
): Array<string> | [] => {
  const namesForShow: Array<string> = [];
  treeItem.treeItems.forEach((item: ITreeNode) => {
    if (selectedCodes[treeItem.type].includes(item.code)) {
      namesForShow.push(item.name);
    }
  });
  return namesForShow;
};

/**
 * Removes the root node identifier from the selected codes array.
 * Filters out specific codes (e.g., 'Channel', 'Business', 'Application') from the selection.
 *
 * @param {Array<string>} selectedCodes - The array of selected filter codes
 * @param {string} removeCode - The code to remove from the selected codes (e.g., 'Channel', 'Business', 'Application')
 * @returns {Array<string>} Filtered array of selected codes for the request payload
 */

export const filterIds = (selectedCodes: Array<string>, removeCode: string): Array<string> => {
  selectedCodes =
    selectedCodes.length > 0
      ? selectedCodes.filter((selectedCode: string) => selectedCode !== removeCode)
      : selectedCodes;
  return selectedCodes;
};

/**
 * Checks if filter data is available to determine whether to show "no data found" message.
 * Validates that at least one filter category (business or channels) has data.
 *
 * @param {IProductFilterOptions} filterOptions - Object containing channel and business filter options
 * @returns {boolean} True if data is available in either business or channels, false otherwise
 */

export const isFilterDataAvailable = (filterOptions: IProductFilterOptions): boolean =>
  filterOptions.business.length > 0 || filterOptions.channels.length > 0;

/**
 * Validates the configuration name during configuration creation.
 * Checks if name is empty or meets length and format requirements (3-40 chars, alphanumeric with spaces/hyphens/underscores).
 *
 * @param {string} name - The name of the configuration to validate
 * @returns {boolean} True if name is valid (empty or meets format requirements), false otherwise
 */

export const validateName = (name: string): boolean => {
  return name === '' || (name.length > 2 && name.length <= 40 && configNameRegEx.test(name));
};

/**
 * Formats a date string to a localized format (DD-MM-YYYY HH:MM:SS).
 * Converts date to GB locale format with specific formatting rules.
 *
 * @param {string} dateValue - The date string to format
 * @returns {string} Formatted date string in DD-MM-YYYY HH:MM:SS format
 */

export const formatDateString = (dateValue: string) => {
  return new Date(dateValue)
    .toLocaleString('en-GB', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    .toString()
    .replace('/', '-')
    .replace(',', '');
};

/**
 * Maps numeric state values to their corresponding configuration type labels.
 * Converts state codes (0-3) to human-readable configuration status strings.
 *
 * @param {number} state - The numeric state value (0: Active, 1: ReadOnly, 2: Temp, 3: Unavailable, 4: Frozen)
 * @returns {string} The corresponding configuration type label, or empty string if state is invalid
 */

export const addStateValues = (state: number) => {
  switch (state) {
    case 0:
      return EConfigurationType.Active;
    case 1:
      return EConfigurationType.ReadOnly;
    case 2:
      return EConfigurationType.Temp;
    case 3:
      return EConfigurationType.UnAvailable;
    case 4:
      return EConfigurationType.Frozen;
    default:
      return '';
  }
};

/**
 * Extracts pagination details from an Axios response.
 * Parses the x-pagination header to retrieve total record count and combines with page/limit info.
 *
 * @param {AxiosResponse} response - The Axios response object containing pagination headers
 * @param {number} page - The current page number
 * @param {number} limit - The number of records per page
 * @returns {Object} Object containing page number, total records count, and limit
 * @returns {number} returns.page - Current page number
 * @returns {number} returns.totalRecords - Total number of records available
 * @returns {number} returns.limit - Records per page limit
 */

export const getPageDetails = (response: AxiosResponse, page: number, limit: number) => {
  const total = response.headers['x-pagination']
    ? JSON.parse(response.headers['x-pagination']).total
    : 0;
  return { page: page, totalRecords: total || 0, limit: limit };
};
